import { TachographDataFile } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { chunkArray } from "@/utilities/chunkArray";

const RESULTS_LIMIT = 50000;

/** The API takes the ids as a list, so long fleets are asked for in chunks. */
const ID_CHUNK_SIZE = 5000;

const DEFAULT_MONTHS_BACK = 12;

/**
 * The tachograph readouts of the given vehicles and drivers. Binary content is left out —
 * it is only fetched for the single file a user actually downloads.
 *
 * The window is `fromTimestamp`: `fromActivityTimestampEnd` cannot be combined with any other
 * criterion ("If FromActivityTimestampEnd is specified all other search criteria should be left
 * default"), so together with the device or driver filter it fails the whole request.
 */
export function useTachographDataFiles(
  deviceIds: string[],
  driverIds: string[],
  monthsBack = DEFAULT_MONTHS_BACK,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["tachographDataFiles", activeSessionId, deviceIds, driverIds, monthsBack],

    queryFn: async ({ signal }) => {
      const since = new Date();
      since.setMonth(since.getMonth() - monthsBack);

      const deviceRequests = chunkArray(deviceIds, ID_CHUNK_SIZE).map(ids =>
        geotabClient.get(
          "TachographDataFile",
          {
            deviceSearch: { deviceIds: ids },
            type: "Device",
            fromTimestamp: since,
            includeBinaryData: false,
          },
          RESULTS_LIMIT,
          signal,
        ),
      );

      const driverRequests = chunkArray(driverIds, ID_CHUNK_SIZE).map(ids =>
        geotabClient.get(
          "TachographDataFile",
          {
            userSearch: { userIds: ids },
            type: "Driver",
            fromTimestamp: since,
            includeBinaryData: false,
          },
          RESULTS_LIMIT,
          signal,
        ),
      );

      const [deviceFiles, driverFiles] = await Promise.all([
        Promise.all(deviceRequests).then(pages => pages.flat()),
        Promise.all(driverRequests).then(pages => pages.flat()),
      ]);

      return { deviceFiles, driverFiles } as {
        deviceFiles: TachographDataFile[];
        driverFiles: TachographDataFile[];
      };
    },

    enabled: deviceIds.length > 0 || driverIds.length > 0,
  });
}
